<template>
    <div class="w-full p-6">
        <div class="flex items-center gap-8 mb-6">
            <div class="flex-1">
                <h1 class="text-2xl font-bold mb-2">
                    Forecasting for PCO -
                    <span class="text-primary">
            {{
                        forecastingPcoTab === 'dashboard' ? 'Dashboard' :
                            forecastingPcoTab === 'calendar' ? 'Calendar' :
                                forecastingPcoTab === 'documents' ? 'Documents' :
                                    'Presentation'
                        }}
          </span>
                </h1>
                <p class="text-muted-foreground">
                    {{
                    forecastingPcoTab === 'dashboard' ? 'Analyze and manage financial forecasts for the Planning and Controlling Office' :
                        forecastingPcoTab === 'calendar' ? 'View and manage your forecasting calendar and upcoming meetings' :
                            forecastingPcoTab === 'documents' ? 'Track and manage your forecasting documents and their status' :
                                'Create and manage presentation slides for your forecasting reports'
                    }}
                </p>
            </div>

            <!-- Tab Navigation -->
            <div class="flex-shrink-0">
                <nav class="flex space-x-6">
                    <button
                            @click="navigateToTab('dashboard')"
                            class="py-2 px-4 font-medium text-sm focus:outline-none"
                            :class="forecastingPcoTab === 'dashboard' ? 'border-b-2 border-primary text-primary' : 'text-muted-foreground hover:text-foreground hover:border-b-2 hover:border-muted-foreground'"
                    >
                        Dashboard
                    </button>
                    <button
                            @click="navigateToTab('calendar')"
                            class="py-2 px-4 font-medium text-sm focus:outline-none"
                            :class="forecastingPcoTab === 'calendar' ? 'border-b-2 border-primary text-primary' : 'text-muted-foreground hover:text-foreground hover:border-b-2 hover:border-muted-foreground'"
                    >
                        Calendar
                    </button>
                    <button
                            @click="navigateToTab('documents')"
                            class="py-2 px-4 font-medium text-sm focus:outline-none"
                            :class="forecastingPcoTab === 'documents' ? 'border-b-2 border-primary text-primary' : 'text-muted-foreground hover:text-foreground hover:border-b-2 hover:border-muted-foreground'"
                    >
                        Documents
                    </button>
                    <button
                            @click="navigateToTab('presentation')"
                            class="py-2 px-4 font-medium text-sm focus:outline-none"
                            :class="forecastingPcoTab === 'presentation' ? 'border-b-2 border-primary text-primary' : 'text-muted-foreground hover:text-foreground hover:border-b-2 hover:border-muted-foreground'"
                    >
                        Presentation
                    </button>
                </nav>
            </div>
        </div>

        <!-- Dashboard Tab Content -->
        <ForecastingPCODashboard v-if="forecastingPcoTab === 'dashboard'"/>

        <!-- Calendar Tab Content -->
        <ForecastingPCOCalendar v-if="forecastingPcoTab === 'calendar'"
                                :userData="userData"/>

        <!-- Documents Tab Content -->
        <ForecastingPCODocuments
                v-if="forecastingPcoTab === 'documents'"
                @open-email-modal="openEmailReviewModal"
                @open-spreadsheet-modal="openSpreadsheetReviewModal"
        />

        <!-- Presentation Tab Content -->
        <ForecastingPCOPresentation v-if="forecastingPcoTab === 'presentation'"/>

        <!-- Email Review Modal -->
        <EmailReviewModal
                :is-open="showEmailReviewModal"
                :process-step-type="currentProcessStepType"
                :recipient-email="recipientEmail"
                :template-variables="templateVariables"
                @close="closeEmailReviewModal"
                @send-success="handleEmailSent"
        />

        <!-- Spreadsheet Review Modal -->
        <SpreadsheetReviewModal
                :is-open="showSpreadsheetReviewModal"
                :document-data="selectedDocument || {}"
                @close="closeSpreadsheetReviewModal"
                @save="handleSpreadsheetSaved"
        />
    </div>
</template>

<script lang="ts">
import {defineComponent, ref, onMounted, PropType, watch} from 'vue';
import {useRouter} from 'vue-router';
import {useNavigation} from '@/composables/useNavigation';
import EmailReviewModal from './EmailReviewModal.vue';
import SpreadsheetReviewModal from './SpreadsheetReviewModal.vue';
import { DOCUMENT_STATUS } from '@/types/document';
import { useDocuments, useForecastingProcess } from '@/composables/useDatabase';
import { useTasks } from '@/composables/useTasks';
import { useDueDates } from '@/composables/useDueDates';
import { ACTIONS } from '@/types/taskActions';
import { logger, LogCategory } from '@/services/logger';
import { supabase } from '@/services/supabase';
import ForecastingPCODashboard from './ForecastingPCODashboard.vue';
import ForecastingPCOCalendar from './ForecastingPCOCalendar.vue';
import ForecastingPCODocuments from './ForecastingPCODocuments.vue';
import ForecastingPCOPresentation from './ForecastingPCOPresentation.vue';
import {BusinessRole} from "@/types/businessRole";
import {UserData} from "@/components/Chatbot.vue";

export default defineComponent({
    name: 'ForecastingPcoView',
    components: {
        EmailReviewModal,
        SpreadsheetReviewModal,
        ForecastingPCODashboard,
        ForecastingPCOCalendar,
        ForecastingPCODocuments,
        ForecastingPCOPresentation
    },
    props: {
        userData: {
            type: Object as PropType<UserData>,
            default: () => ({
                name: 'User',
                avatar: null,
                business_role: BusinessRole.PLANNER
            })
        }
    },
    setup() {
        const router = useRouter();
        const {forecastingPcoTab} = useNavigation();
        const showEmailReviewModal = ref(false);
        const showSpreadsheetReviewModal = ref(false);
        const currentProcessStepType = ref('DOWNLOAD');
        const recipientEmail = ref('andrej.svetina@fraport-slovenija.si');
        const templateVariables = ref<Record<string, any>>({});

        // Use the documents composable to fetch data from the database
        const {
            selectedDocument,
            fetchCurrentDocuments
        } = useDocuments();

        // Use the forecasting process composable
        const { fetchProcessById } = useForecastingProcess();

        // Fetch documents and initialize tabwhen component is mounted
        onMounted(async () => {// Initialize the tab from URL query parameter if available
      const tabFromQuery = router.currentRoute.value.query.tab;
      if (tabFromQuery && typeof tabFromQuery === 'string') {
        const validTabs = ['dashboard', 'calendar', 'documents', 'presentation'];
        if (validTabs.includes(tabFromQuery)) {
          forecastingPcoTab.value = tabFromQuery;
        }
      }
            await fetchCurrentDocuments();// Mark the current time as the last successful navigation
      // This will help prevent session validation issues
      if (window.localStorage) {
        window.localStorage.setItem('last_pco_tab_navigation', Date.now().toString());
      }

      // Return cleanup function to run when component is unmounted
      return () => {
        // Reset any stuck loading states when component is unmounted
        const { loading } = useForecastingProcess();
        if (loading.value) {
          logger.info('Resetting loading states during component unmount', LogCategory.GENERAL);
          loading.value = false;
        }

        logger.info('ForecastingPCO component unmounted', LogCategory.GENERAL);
      };
    });

    // Watch for route query changes (when user uses browser navigation)
    watch(() => router.currentRoute.value.query.tab, (newTab) => {
      if (newTab && typeof newTab === 'string') {
        const validTabs = ['dashboard', 'calendar', 'documents', 'presentation'];
        if (validTabs.includes(newTab) && forecastingPcoTab.value !== newTab) {
          forecastingPcoTab.value = newTab;
        }
      }
        });

        // Function to navigate between tabswith improved performance
        const navigateToTab = (tab: string) => {
            // Only update if the tab has changed
      if (forecastingPcoTab.value !== tab) {
        // Reset any stuck loading states before changing tabs
        if (forecastingPcoTab.value === 'documents' && tab !== 'documents') {
          // Get the forecasting process composable to reset its loading state
          const { loading } = useForecastingProcess();
          if (loading.value) {
            logger.warn('Resetting stuck loading state during tab navigation', LogCategory.GENERAL);
            loading.value = false;
          }
        }

        // Set the active tab in the navigation state
        forecastingPcoTab.value = tab;

        // Update URL to reflect the change without triggering a full navigation
        // This uses history.replaceState to avoid router navigation events
        const url = new URL(window.location.href);
        url.searchParams.set('tab', tab);
        window.history.replaceState({}, '', url.toString());

        // Log navigation for debugging
        logger.info('Tab changed', { tab, previous: forecastingPcoTab.value });

        // Navigate to the corresponding route
        router.push(`/forecasting-pco/${tab}`);
      }
        };

        /**
         * Formats a date range in German format (DD.MM.YYYY - DD.MM.YYYY)
         * @param startDate Start date string
         * @param endDate End date string
         * @returns Formatted date range string
         */
        const formatDateRange = (startDate: string, endDate: string): string => {
            if (!startDate || !endDate) return 'Date range not available';
            
            const formatDate = (dateStr: string): string => {
                const date = new Date(dateStr);
                return date.toLocaleDateString('de-DE', {
                    day: '2-digit',
                    month: '2-digit',
                    year: 'numeric'
                });
            };
            
            return `${formatDate(startDate)} - ${formatDate(endDate)}`;
        };

        /**
         * Fetches forecasting process data using the existing composable
         * @param forecastId The forecast ID to fetch data for
         * @returns Promise resolving to forecasting process data or null if not found/error
         */
        const fetchForecastingProcessData = async (forecastId: string) => {
            if (!forecastId) {
                logger.warn('No forecast ID provided for fetching forecasting process data', LogCategory.GENERAL);
                return null;
            }

            try {
                logger.info('Fetching forecasting process data using composable', LogCategory.GENERAL, {
                    forecastId
                });

                const processData = await fetchProcessById(forecastId);

                if (processData) {
                    logger.info('Successfully fetched forecasting process data', LogCategory.DATABASE, {
                        processName: processData.name,
                        startDate: processData.start_date,
                        endDate: processData.end_date
                    });
                    return processData;
                } else {
                    logger.warn('No forecasting process data found', LogCategory.DATABASE, {
                        forecastId
                    });
                    return null;
                }
            } catch (error) {
                logger.error('Exception while fetching forecasting process data', LogCategory.GENERAL, {
                    error,
                    forecastId
                });
                return null;
            }
        };

        // Email Modal functions
        const openEmailReviewModal = async (documentParam = null) => {
            // Use the provided document or fall back to selectedDocument
            if (!documentParam && !selectedDocument.value) {
                console.error('No document selected');
                return;
            }

            // Extract data from the selected document to populate template variables
            const document = documentParam || selectedDocument.value;

            console.log('Opening email review modal for document:', document);

            // For documents with status EMAIL_PENDING, fetch planners with matching report_type
            if (document.status === DOCUMENT_STATUS.EMAIL_PENDING && document.report_type) {
                try {
                    logger.info('Fetching planners with matching report_type', LogCategory.GENERAL, {
                        documentId: document.id,
                        reportType: document.report_type
                    });

                    // Fetch users from profiles table with matching report_type
                    const {data: plannersData, error: plannersError} = await supabase
                        .from('profiles')
                        .select('id, name, email, avatar_url')
                        .eq('report_type', document.report_type);

                    if (!plannersError && plannersData && plannersData.length > 0) {
                        // Use the first planner's email as recipient
                        recipientEmail.value = plannersData[0].email || '';

                        // Store planner information for later use
                        const plannerInfo = {
                            planners: plannersData,
                            plannerName: plannersData[0].name || '',
                            plannerAvatarUrl: plannersData[0].avatar_url || '',
                            avatarUrl: plannersData[0].avatar_url || '',
                            firstName: plannersData[0].name?.split(' ')[0] || '',
                            lastName: plannersData[0].name?.split(' ').slice(1).join(' ') || '',
                            fullName: plannersData[0].name || ''
                        };

                        // Store for later use when building the full templateVariables
                        Object.assign(templateVariables.value, plannerInfo);

                        logger.info('Found planners with matching report_type', LogCategory.GENERAL, {
                            count: plannersData.length,
                            firstPlannerEmail: recipientEmail.value,
                            plannerName: plannerInfo.plannerName
                        });
                    } else {
                        // Fallback to created_by user if no matching planners found
                        logger.warn('No planners found with matching report_type, falling back to created_by', LogCategory.GENERAL, {
                            error: plannersError,
                            reportType: document.report_type
                        });
                        await fetchCreatedByUserEmail(document);
                    }
                } catch (error) {
                    logger.error('Error fetching planners with matching report_type', LogCategory.GENERAL, {
                        error,
                        reportType: document.report_type
                    });
                    await fetchCreatedByUserEmail(document);
                }
            } else {
                // For other document statuses, use the created_by user's email
                await fetchCreatedByUserEmail(document);
            }

            // Fetch comments for the document
            const {comments} = useDocuments();

            // Save the current planner information
            const currentPlannerInfo = {
                planners: templateVariables.value.planners,
                plannerName: templateVariables.value.plannerName,
                plannerAvatarUrl: templateVariables.value.plannerAvatarUrl,
                avatarUrl: templateVariables.value.avatarUrl,
                firstName: templateVariables.value.firstName,
                lastName: templateVariables.value.lastName,
                fullName: templateVariables.value.fullName
            };

            // Fetch forecasting process data before populating template variables
            const forecastingProcessData = await fetchForecastingProcessData(document.forecast_id);

            // Populate template variables with document data
            templateVariables.value = {
                // Basic document information
                documentName: document.name,
                documentId: document.id,
                documentDescription: document.description,
                documentStatus: document.status,
                reportType: document.report_type,

                // Process information
                processName: forecastingProcessData?.name || 'Forecasting Process',

                // New forecasting process variables
                forecastName: forecastingProcessData?.name || 'Forecasting Process',
                forecastPeriod: forecastingProcessData?.start_date && forecastingProcessData?.end_date
                    ? formatDateRange(forecastingProcessData.start_date, forecastingProcessData.end_date)
                    : document.period || document.process_name || 'Current Period',

                // Restore planner information that was set earlier
                ...currentPlannerInfo,

                // Debug information
                debug_has_avatar: !!document.created_by_avatar_url,

                // Company information
                companyName: 'Fraport',

                // Additional data that might be in the document
                forecastData: document.forecast_data || document.data || 'Please review the attached forecast data',
                dueDate: document.due_date ? new Date(document.due_date).toLocaleDateString('en-US', {
                    year: 'numeric',
                    month: 'long',
                    day: 'numeric'
                }) : 'as soon as possible',

                // Comments if available
                comments: comments.value.length > 0 ?
                    comments.value.map(c => `${c.created_by_name}: ${c.content}`).join('\n') :
                    'No comments available'
            };

            console.log('Template variables populated for email review modal:', templateVariables.value);

            // Set the current process step type based on document status
            switch (document.status) {
                case DOCUMENT_STATUS.CREATING:
                    currentProcessStepType.value = 'DOWNLOAD';
                    break;
                case DOCUMENT_STATUS.READY_FOR_DOWNLOAD:
                    currentProcessStepType.value = 'DOWNLOAD';
                    break;
                case DOCUMENT_STATUS.DOWNLOADED:
                    currentProcessStepType.value = 'UPLOAD';
                    break;
                case DOCUMENT_STATUS.REVISION_NEEDED:
                    currentProcessStepType.value = 'REVISION';
                    break;
                case DOCUMENT_STATUS.ADJUSTMENT_NEEDED:
                    currentProcessStepType.value = 'ADJUST';
                    break;
                case DOCUMENT_STATUS.PRESENTATION:
                    currentProcessStepType.value = 'PRESENT';
                    break;
                default:
                    currentProcessStepType.value = 'DOWNLOAD';
            }

            // Open the modal
            showEmailReviewModal.value = true;
        };

        // Helper function to fetch the created_by user's email
        const fetchCreatedByUserEmail = async (document) => {
            if (document.created_by) {
                try {
                    const {data: profileData, error: profileError} = await supabase
                        .from('profiles')
                        .select('email, name, avatar_url')
                        .eq('id', document.created_by)
                        .single();

                    if (!profileError && profileData && profileData.email) {
                        recipientEmail.value = profileData.email;

                        // Store planner information for later use
                        const plannerInfo = {
                            plannerName: profileData.name || '',
                            plannerAvatarUrl: profileData.avatar_url || '',
                            avatarUrl: profileData.avatar_url || '',
                            firstName: profileData.name?.split(' ')[0] || '',
                            lastName: profileData.name?.split(' ').slice(1).join(' ') || '',
                            fullName: profileData.name || ''
                        };

                        // Store for later use when building the full templateVariables
                        Object.assign(templateVariables.value, plannerInfo);

                        logger.info('Setting recipient email to created_by user', LogCategory.GENERAL, {
                            email: profileData.email,
                            plannerName: plannerInfo.plannerName
                        });
                    } else {
                        // Fallback to default
                        recipientEmail.value = '';
                        logger.warn('Error fetching email or no email found for created_by user', LogCategory.GENERAL, {
                            error: profileError
                        });
                    }
                } catch (error) {
                    logger.error('Error fetching user profile for created_by user', LogCategory.GENERAL, {
                        error
                    });
                    recipientEmail.value = '';
                }
            } else {
                // Fallback to default
                recipientEmail.value = '';
                logger.warn('No created_by user ID found, using default empty email', LogCategory.GENERAL);
            }
        };

        const closeEmailReviewModal = () => {
            showEmailReviewModal.value = false;
            // Reset template variables when modal is closed
            templateVariables.value = {};
        };


        /**
         * Creates a download task for the email recipient
         * @param email The recipient's email address
         * @param documentId The document ID
         * @param documentName The document name
         * @returns Promise resolving to the created task or null if failed
         */
        const createDownloadTask = async (email: string, documentId: string, documentName: string) => {
            if (!email || !documentId) {
                logger.error('Cannot create task: Missing email or document ID', LogCategory.GENERAL);
                return null;
            }

            try {
                // Get the tasks composable
                const {createTask} = useTasks();
        

        // Calculate due date using the getDueDate function
        const { getDueDate } = useDueDates();
        const dueDateString = await getDueDate(ACTIONS.DOWNLOAD, documentId);

                // Find the user ID associated with the recipient email
                const {data: userData, error: userError} = await supabase
                    .from('profiles')
                    .select('id')
                    .eq('email', email)
                    .single();

                if (userError) {
                    logger.error('Error finding user ID for recipient email:', LogCategory.DATABASE, {
                        error: userError
                    });
                    return null;
                }

                if (!userData || !userData.id) {
                    logger.error('No user found with email:', LogCategory.GENERAL, {
                        email
                    });
                    return null;
                }

                // Create the task with the correct user ID
                const taskResult = await createTask({
                    action: 'DOWNLOAD',
                    assigned_user_id: userData.id, // Use the ID of the email recipient
                    document_id: documentId,
                    due_at: dueDateString,
                    description: `Please download and review the document "${documentName}" by ${new Date(dueDateString).toLocaleDateString()}`
                });

                if (taskResult) {
                    logger.info('Task created successfully:', LogCategory.DATABASE, {
                        result: taskResult
                    });
                    return taskResult;
                } else {
                    logger.error('Failed to create task:', LogCategory.DATABASE, {
                        documentId,
                        recipientEmail: email
                    });
                    return null;
                }
            } catch (error) {
                logger.error('Error creating task:', LogCategory.GENERAL, {
                    error
                });
                return null;
            }
        };

        const handleEmailSent = async () => {
            // Log a message to the console when email is sent successfully
            console.log('Email sent successfully! The send-success event was received from EmailReviewModal.');

            // Get document info from template data, because selectedDocument might be null
            const documentId = templateVariables.value.documentId || selectedDocument.value?.id;
            const documentName = templateVariables.value.documentName || selectedDocument.value?.name;

            // Create a task for the user to download the document
            if (documentId && documentName && recipientEmail.value) {
                await createDownloadTask(
                    recipientEmail.value,
                    documentId,
                    documentName
                );
            } else {
                logger.error('Missing document ID or name, cannot create download task', LogCategory.GENERAL, {
                    documentId,
                    documentName,
                    recipientEmail: recipientEmail.value
                });
            }

            // Update UI to reflect that the email was sent
            logger.info('Email was sent successfully', LogCategory.GENERAL, {
                documentId: selectedDocument.value?.id,
                recipientEmail: recipientEmail.value
            });

            // Close the modal
            closeEmailReviewModal();

            // Refresh documents to reflect any status changes
            fetchCurrentDocuments();
        };

        // Spreadsheet Modal functions
        const openSpreadsheetReviewModal = (documentParam = null) => {
            // If a document is provided, update the selectedDocument
            if (documentParam) {
                selectedDocument.value = documentParam;
            }

            // Only open the modal if we have a selected document
            if (selectedDocument.value) {
                showSpreadsheetReviewModal.value = true;
            } else {
                console.error('No document selected for spreadsheet review');
            }
        };

        const closeSpreadsheetReviewModal = () => {
            showSpreadsheetReviewModal.value = false;
        };

        const handleSpreadsheetSaved = async (updatedDocument) => {
            console.log('Spreadsheet data was saved successfully');
            logger.info('Document status updated', LogCategory.GENERAL, {
                documentId: updatedDocument.id,
                previousStatus: selectedDocument.value?.status,
                newStatus: updatedDocument.status
            });

            // Update the document in the database with the new status
            try {
                // eslint-disable-next-line @typescript-eslint/no-unused-vars
                const {data, error} = await supabase
                    .from('documents')
                    .update({status: DOCUMENT_STATUS.EMAIL_PENDING})
                    .eq('id', updatedDocument.id)
                    .select();

                if (error) {
                    logger.error('Failed to update document status', LogCategory.GENERAL, {
                        error,
                        documentId: updatedDocument.id
                    });
                } else {
                    logger.info('Document status updated in database', LogCategory.GENERAL, {
                        documentId: updatedDocument.id,
                        status: DOCUMENT_STATUS.EMAIL_PENDING
                    });

                    // Refresh documents to reflect the status change
                    await fetchCurrentDocuments();
                }
            } catch (error) {
                logger.error('Error updating document status', LogCategory.GENERAL, {
                    error,
                    documentId: updatedDocument.id
                });
            }
        };

        return {
            forecastingPcoTab,
            navigateToTab,
            showEmailReviewModal,
            showSpreadsheetReviewModal,
            currentProcessStepType,
            recipientEmail,
            templateVariables,
            selectedDocument,
            openEmailReviewModal,
            closeEmailReviewModal,
            handleEmailSent,
            createDownloadTask,
            openSpreadsheetReviewModal,
            closeSpreadsheetReviewModal,
            handleSpreadsheetSaved
        };
    }
});
</script>
